/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { makeWedgePath } from './utils';
import { styles } from './themes/PieChartStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';

/**
 * The props for pie slice.
 */
type PieSliceProps = {
  /**
   * The x coordinate of the center of the slice.
   */
  cx: number;

  /**
   * The y coordinate of the center of the slice.
   */
  cy: number;

  /**
   * The radius of the pie slice.
   */
  r: number;

  /**
   * The start angle of the pie slice.
   */
  startAngle: number;

  /**
   * The angle extent of the pie slice.
   */
  angleExtent: number;

  /**
   * The gaps to keep between the pie slice items.
   */
  gap: number;

  /**
   * The inner radius of the pie slice.
   */
  innerRadius: number;

  /**
   * The color of the pie slice.
   */
  color: ColorProps['color'];

  /**
   * Whether the slice is focused.
   */
  isFocused?: boolean;

  /**
   * Whether the slice is hovered.
   */
  isHovered?: boolean;

  /**
   * The id of the pie slice.
   */
  id?: string;

  /**
   * The index of the item among rendered series.
   */
  itemIndex: number;

  /**
   * The aria label of the pie slice.
   */
  accessibleLabel?: string;
};

/**
 * The Pie Slice component.
 */
export function PieSlice({
  cx,
  cy,
  r,
  startAngle,
  angleExtent,
  gap,
  innerRadius,
  color,
  id,
  itemIndex,
  isFocused = false,
  isHovered = false,
  ...props
}: PieSliceProps) {
  const path = makeWedgePath(0, cx, cy, r, r, innerRadius, angleExtent, gap, startAngle);
  let innerPath;
  if (isFocused) {
    innerPath = makeWedgePath(2, cx, cy, r, r, innerRadius, angleExtent, gap, startAngle);
    return (
      <g
        id={id}
        role={props.accessibleLabel ? 'img' : undefined}
        data-oj-item-index={itemIndex}
        data-oj-object="pieSlice"
        aria-label={props.accessibleLabel}>
        <path d={path} fill={color} stroke={color} className={styles.innerShapes} />
        <path
          d={innerPath}
          fill={color}
          className={classNames([styles.contrastBorder, styles.innerShapes])}
        />
      </g>
    );
  }
  return (
    <path
      role={props.accessibleLabel ? 'img' : undefined}
      aria-label={props.accessibleLabel}
      d={path}
      data-oj-object="pieSlice"
      fill={color}
      id={id}
      data-oj-item-index={itemIndex}
    />
  );
}
